/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, createInventoryAvailabilitySearchCriteria } from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MsDyn365 from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    AttributeValue,
    CartLine,
    PriceLookupContext,
    ProductPrice,
    ProductSearchResult,
    ProductUnitOfMeasure,
    ProductWarehouseInventoryAvailability,
    ProjectionDomain,
    SearchArea
} from '@msdyn365-commerce/retail-proxy';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    getAttributeValuesAsync,
    getEstimatedAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import React, { MouseEventHandler, useEffect, useState } from 'react';

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { getActivePricesAction, GetActivePricesInput } from '../../../actions/getactiveprices.action';
import { GetProductUnitOfMeasureInput, getProductUnitsOfMeasureAction } from '../../../actions/getproductunitsofmeasure.action';
import {
    ISearchResultContainerProps,
    ISearchResultContainerResources
} from '../definition-extensions/search-result-container.ext.props.autogenerated';
import { ProductComponent, UomAwarePriceStatus } from './components/product.component';

enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

type AddToCartQueueHandler = (cartLines: CartLine[]) => Promise<void>;

interface IAddToCartQueueItem {
    execute: () => Promise<void>;
    resolve: () => void;
    reject: (error: unknown) => void;
}

interface IPreferredStoreStorageValue {
    StoreId?: string;
}

interface ISelectedStoreAvailabilityState {
    requestKey: string;
    availabilityByProduct: Map<number, ProductWarehouseInventoryAvailability>;
}

interface IProductPricingPair {
    productId: number;
    unitOfMeasure: string;
}

interface IProductPricingState {
    unitOfMeasure?: string;
    status: UomAwarePriceStatus;
    price?: ProductPrice;
}

interface IProductPricingRequest {
    requestId: number;
    unitOfMeasure: string;
}

interface IProductPriceCacheEntry {
    status: 'resolved' | 'failed';
    price?: ProductPrice;
}

const normalizePricingUom = (unitOfMeasure?: string): string | undefined => {
    const normalized = unitOfMeasure?.trim().toLocaleUpperCase();
    return normalized || undefined;
};

const isSamePricingUom = (left?: string, right?: string): boolean => normalizePricingUom(left) === normalizePricingUom(right);

type PricingUomResolvedHandler = (productId: number, unitOfMeasure?: string) => void;

interface IPlpPricingContext {
    uomByProduct: ReadonlyMap<number, string | undefined>;
    pricingByProduct: ReadonlyMap<number, IProductPricingState>;
    onPricingUomResolved: PricingUomResolvedHandler;
}

interface IPlpDeferredProductDataContext {
    loadProductAttributes: (productId: number) => Promise<AttributeValue[] | null>;
    loadProductUnitsOfMeasure: (productId: number) => Promise<ProductUnitOfMeasure[] | null>;
}

interface IDeferredProductCardProps {
    className: string;
    children: (shouldLoadDeferredData: boolean) => React.ReactNode;
}

const PLP_DEFERRED_DATA_ROOT_MARGIN = '600px 0px';
const INITIAL_ATTRIBUTE_PREFETCH_COUNT = 10;
const ATTRIBUTE_PREFETCH_CONCURRENCY = 4;

const hasRequiredSearchAttributes = (product: ProductSearchResult): boolean => {
    const attributes = product.AttributeValues ?? [];
    let hasBrand = false;
    let hasProductBadge = false;

    attributes.forEach(attribute => {
        const name = attribute.Name ?? attribute.KeyName;
        if (name === 'Brand' && typeof attribute.TextValue === 'string' && attribute.TextValue.trim()) {
            hasBrand = true;
        }
        if (name === 'Product Badge' && attribute.BooleanValue !== undefined) {
            hasProductBadge = true;
        }
    });

    return hasBrand && hasProductBadge;
};

const DeferredProductCard: React.FC<IDeferredProductCardProps> = ({ className, children }) => {
    const cardRef = React.useRef<HTMLLIElement>(null);
    const [shouldLoadDeferredData, setShouldLoadDeferredData] = React.useState(false);

    React.useEffect(() => {
        if (!MsDyn365.isBrowser || typeof window === 'undefined' || !cardRef.current) {
            return;
        }

        if (typeof window.IntersectionObserver === 'undefined') {
            setShouldLoadDeferredData(true);
            return;
        }

        const observer = new window.IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    setShouldLoadDeferredData(true);
                    observer.disconnect();
                }
            },
            { rootMargin: PLP_DEFERRED_DATA_ROOT_MARGIN }
        );
        observer.observe(cardRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <li className={className} ref={cardRef}>
            {children(shouldLoadDeferredData)}
        </li>
    );
};

const addToCartQueue: IAddToCartQueueItem[] = [];
const addToCartQueueDrainResolvers: (() => void)[] = [];
let isProcessingAddToCartQueue = false;

const getPreferredStoreId = (): string => {
    if (!MsDyn365.isBrowser || typeof window === 'undefined') {
        return '';
    }

    try {
        const storedValue = window.localStorage.getItem('_msdyn365__preferredStore_');
        if (!storedValue) {
            return '';
        }

        const preferredStore = JSON.parse(storedValue) as unknown;
        if (!preferredStore || typeof preferredStore !== 'object') {
            return '';
        }

        const storeId = (preferredStore as IPreferredStoreStorageValue).StoreId;
        return typeof storeId === 'string' ? storeId : '';
    } catch (error) {
        console.error(error instanceof Error ? error.message : 'Unable to parse the preferred store.');
        return '';
    }
};

const waitForAddToCartQueueToDrain = (): Promise<void> => {
    if (!isProcessingAddToCartQueue && addToCartQueue.length === 0) {
        return Promise.resolve();
    }

    return new Promise<void>(resolve => {
        addToCartQueueDrainResolvers.push(resolve);
    });
};

const processAddToCartQueue = async (): Promise<void> => {
    if (isProcessingAddToCartQueue) {
        return;
    }

    isProcessingAddToCartQueue = true;

    try {
        while (addToCartQueue.length > 0) {
            const queueItem = addToCartQueue.shift();
            if (!queueItem) {
                continue;
            }

            try {
                await queueItem.execute();
                queueItem.resolve();
            } catch (error) {
                queueItem.reject(error);
            }
        }
    } finally {
        isProcessingAddToCartQueue = false;

        if (addToCartQueue.length === 0) {
            addToCartQueueDrainResolvers.splice(0).forEach(resolve => resolve());
        }
    }
};

interface IQueueAwarePaginationProps {
    onClickCapture?: React.MouseEventHandler<HTMLElement>;
}

const renderQueueAwarePagination = (pagination: React.ReactNode, onClickCapture: React.MouseEventHandler<HTMLElement>): React.ReactNode => {
    if (!React.isValidElement<IQueueAwarePaginationProps>(pagination)) {
        return pagination;
    }

    const existingOnClickCapture = pagination.props.onClickCapture;

    return React.cloneElement(pagination, {
        onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
            existingOnClickCapture?.(event);
            if (!event.defaultPrevented) {
                onClickCapture(event);
            }
        }
    });
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
// const renderProductCards = (props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>): JSX.Element | null => {
//     return <Node {...props.ProductsContainer}>{props.products}</Node>;
// };

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    product: ProductSearchResult,
    index: number,
    isEnabledProductDescription: boolean | undefined,
    enqueueAddToCart: AddToCartQueueHandler,
    selectedStoreId: string,
    availabilityByProduct: ReadonlyMap<number, ProductWarehouseInventoryAvailability>,
    pricingContext: IPlpPricingContext,
    deferredProductDataContext: IPlpDeferredProductDataContext
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    // The repository layout is one column on mobile, two columns on tablet, and four on desktop.
    // Keep priority conservative and SSR-safe because the server cannot know the client viewport.
    const isPriorityImage = index < (props.isMobile ? 1 : 6);
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;
    const pricingUom = pricingContext.uomByProduct.get(product.RecordId);
    const hasResolvedPricingUom = pricingContext.uomByProduct.has(product.RecordId);
    const productPricing = pricingContext.pricingByProduct.get(product.RecordId);
    const isCurrentProductPricing = productPricing?.unitOfMeasure === pricingUom;
    let priceStatus: UomAwarePriceStatus = 'loading';
    if (!hasResolvedPricingUom) {
        priceStatus = 'waiting';
    } else if (!pricingUom) {
        priceStatus = 'failed';
    } else if (isCurrentProductPricing) {
        priceStatus = productPricing?.status ?? 'loading';
    }

    return (
        <DeferredProductCard className={classname} key={product.RecordId}>
            {shouldLoadDeferredData => (
                <ProductComponent
                    context={context}
                    telemetryContent={telemetryContent}
                    imageSettings={config.imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    allowBack={isAllowBack}
                    id={props.id}
                    key={product.RecordId}
                    typeName={props.typeName}
                    data={{ product }}
                    quickViewButton={quickviewslot}
                    productComparisonButton={productComparisonButton}
                    isEnabledProductDescription={isEnabledProductDescription}
                    inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                    isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                    priceResources={priceResources}
                    swatchItemAriaLabel={resources.swatchItemAriaLabel}
                    dimensionAvailabilities={productsDimensionAvailabilities?.find(
                        (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                            ArrayExtensions.hasElements(dimensionAvailability) &&
                            dimensionAvailability[0].masterProductId === product.MasterProductId
                    )}
                    enqueueAddToCart={enqueueAddToCart}
                    isStoreSelected={Boolean(selectedStoreId)}
                    storeId={selectedStoreId}
                    selectedStoreAvailability={availabilityByProduct.get(product.RecordId)}
                    uomAwarePrice={priceStatus === 'resolved' ? productPricing?.price : undefined}
                    uomAwarePriceStatus={priceStatus}
                    isPriorityImage={isPriorityImage}
                    onPricingUomResolved={pricingContext.onPricingUomResolved}
                    shouldLoadDeferredData={shouldLoadDeferredData}
                    loadProductAttributes={deferredProductDataContext.loadProductAttributes}
                    loadProductUnitsOfMeasure={deferredProductDataContext.loadProductUnitsOfMeasure}
                />
            )}
        </DeferredProductCard>
    );
};

/**
 * Normalizes a product value or search keyword for matching.
 * @param value - Value to normalize.
 * @returns The normalized value.
 */
const normalizeSearchValue = (value?: string): string => {
    return (value ?? '')
        .toLocaleLowerCase()
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim();
};

/**
 * Determines whether a product is a strong match for all terms in the search keyword.
 * @param product - Product to evaluate.
 * @param searchKeyword - Search keyword from the query string.
 * @returns True when all keyword terms occur in the product name or product number.
 */
const isTopProductForKeyword = (product: ProductSearchResult, searchKeyword: string): boolean => {
    const keywordTerms = normalizeSearchValue(searchKeyword)
        .split(' ')
        .filter(Boolean);
    const productSearchValue = normalizeSearchValue(`${product.Name ?? ''} ${product.ProductNumber ?? ''}`);

    return keywordTerms.length > 0 && keywordTerms.every(term => productSearchValue.includes(term));
};

/**
 * Renders one product section.
 * @param props - The view props.
 * @param title - Section heading.
 * @param products - Products in the section.
 * @param isProductDescriptionEnabled - Whether description cards are enabled in module configuration.
 * @param indexOffset - Offset used to preserve the description interval between sections.
 * @returns The product section.
 */
const renderProductSection = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    title: string,
    products: ProductSearchResult[],
    isProductDescriptionEnabled: boolean,
    enqueueAddToCart: AddToCartQueueHandler,
    selectedStoreId: string,
    availabilityByProduct: ReadonlyMap<number, ProductWarehouseInventoryAvailability>,
    pricingContext: IPlpPricingContext,
    deferredProductDataContext: IPlpDeferredProductDataContext,
    indexOffset: number = 0
): JSX.Element | null => {
    if (!ArrayExtensions.hasElements(products)) {
        return null;
    }

    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;

    return (
        <section className='ms-product-search-result__section'>
            {title && <h2 className='ms-product-search-result__section-title'>{title}</h2>}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) => {
                    const productIndex = index + indexOffset;
                    const showDescription =
                        isProductDescriptionEnabled &&
                        (productIndex % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                            productIndex % productDescriptionInterval === productDescriptionIntervalRemainder9);

                    return renderProductCardsDescription(
                        props,
                        product,
                        productIndex,
                        showDescription,
                        enqueueAddToCart,
                        selectedStoreId,
                        availabilityByProduct,
                        pricingContext,
                        deferredProductDataContext
                    );
                })}
            </ul>
        </section>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    enqueueAddToCart: AddToCartQueueHandler,
    selectedStoreId: string,
    availabilityByProduct: ReadonlyMap<number, ProductWarehouseInventoryAvailability>,
    pricingContext: IPlpPricingContext,
    deferredProductDataContext: IPlpDeferredProductDataContext
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }

    const query = props.context.actionContext.requestContext.query;
    const searchKeyword = query?.q?.trim();
    // // @ts-ignore -- enableProdutDescription is supplied by the theme definition extension.
    // const isProductDescriptionEnabled = Boolean(props.config.enableProdutDescription);

    if (searchKeyword) {
        const skip = Number(query?.skip ?? 0);
        const topProducts = skip === 0 ? products.filter(product => isTopProductForKeyword(product, searchKeyword)) : [];
        const topProductIds = new Set(topProducts.map(product => product.RecordId));
        const relatedProducts = products.filter(product => !topProductIds.has(product.RecordId));

        return (
            <Node {...props.ProductsContainer}>
                {props.errorMessage}
                {renderProductSection(
                    props,
                    '',
                    topProducts,
                    false,
                    enqueueAddToCart,
                    selectedStoreId,
                    availabilityByProduct,
                    pricingContext,
                    deferredProductDataContext
                )}
                {renderProductSection(
                    props,
                    '',
                    relatedProducts,
                    false,
                    enqueueAddToCart,
                    selectedStoreId,
                    availabilityByProduct,
                    pricingContext,
                    deferredProductDataContext,
                    topProducts.length
                )}
            </Node>
        );
    }

    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            {renderProductSection(
                props,
                '',
                products,
                false,
                enqueueAddToCart,
                selectedStoreId,
                availabilityByProduct,
                pricingContext,
                deferredProductDataContext
            )}
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return <Node {...TitleContainer}>{title.titleCount}</Node>;
    }
    return null;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const headerContainer: HTMLElement | null = document.querySelector('.ms-header');

    const headerHeight: number | undefined = headerContainer?.offsetHeight;
    const containerHeight: number = headerHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');
    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
            refinerElement.setAttribute('style', `top: ${defaultContainer.clientHeight}px`);
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-top');
        } else {
            refinerElement.classList.remove('lock-top');
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): () => void {
    let animationFrameId: number | undefined;
    const handleScroll = (): void => {
        if (animationFrameId !== undefined) {
            return;
        }

        animationFrameId = window.requestAnimationFrame(() => {
            animationFrameId = undefined;
            configureStickyFilter(isMobile, isStickyHeader);
        });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    configureStickyFilter(isMobile, isStickyHeader);

    return () => {
        window.removeEventListener('scroll', handleScroll);
        if (animationFrameId !== undefined) {
            window.cancelAnimationFrame(animationFrameId);
        }
    };
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        resources
    } = props;
    const activeProducts = props.data.listPageState.result?.activeProducts;
    const activeProductsRef = React.useRef<readonly ProductSearchResult[]>([]);
    activeProductsRef.current = activeProducts ?? [];
    const productIds = activeProducts?.map(product => product.RecordId).filter(productId => Boolean(productId)) ?? [];
    const activeProductIds = Array.from(new Set(productIds));
    const activeProductIdsKey = activeProductIds.join(',');
    const [selectedStoreId, setSelectedStoreId] = React.useState<string>('');
    const [selectedStoreAvailabilityState, setSelectedStoreAvailabilityState] = React.useState<ISelectedStoreAvailabilityState>({
        requestKey: '',
        availabilityByProduct: new Map<number, ProductWarehouseInventoryAvailability>()
    });
    const selectedStoreAvailabilityRequestKey = `${selectedStoreId}|${activeProductIdsKey}`;
    const selectedStoreAvailabilityByProduct =
        selectedStoreAvailabilityState.requestKey === selectedStoreAvailabilityRequestKey
            ? selectedStoreAvailabilityState.availabilityByProduct
            : new Map<number, ProductWarehouseInventoryAvailability>();
    const isAuthenticated = Boolean(props.context.request.user.isAuthenticated);
    const channelId = props.context.request.apiSettings.channelId;
    const catalogId = props.context.request.apiSettings.catalogId;
    const deferredProductDataCacheContextKey = JSON.stringify([
        channelId,
        catalogId,
        props.context.request.locale,
        props.context.request.channel?.Currency ?? '',
        isAuthenticated,
        props.context.request.user.customerAccountNumber ?? ''
    ]);
    const actionContextRef = React.useRef(props.context.actionContext);
    actionContextRef.current = props.context.actionContext;
    const productAttributeCacheRef = React.useRef<Map<string, Promise<AttributeValue[] | null>>>(new Map());
    const productUomCacheRef = React.useRef<Map<string, Promise<ProductUnitOfMeasure[] | null>>>(new Map());
    const loadProductAttributes = React.useCallback(
        (productId: number): Promise<AttributeValue[] | null> => {
            const cacheKey = `${deferredProductDataCacheContextKey}|attributes|${productId}`;
            const cachedRequest = productAttributeCacheRef.current.get(cacheKey);
            if (cachedRequest) {
                return cachedRequest;
            }

            const request = getAttributeValuesAsync({ callerContext: actionContextRef.current }, productId, channelId, catalogId).catch(
                error => {
                    console.error(error instanceof Error ? error.message : 'Unable to load product attributes.');
                    return null;
                }
            );
            productAttributeCacheRef.current.set(cacheKey, request);
            return request;
        },
        [catalogId, channelId, deferredProductDataCacheContextKey]
    );
    const loadProductUnitsOfMeasure = React.useCallback(
        (productId: number): Promise<ProductUnitOfMeasure[] | null> => {
            const cacheKey = `${deferredProductDataCacheContextKey}|uom|quantity-limits|${productId}`;
            const cachedRequest = productUomCacheRef.current.get(cacheKey);
            if (cachedRequest) {
                return cachedRequest;
            }

            const request = getProductUnitsOfMeasureAction(
                new GetProductUnitOfMeasureInput(productId, { IncludeQuantityLimitRules: true }),
                actionContextRef.current
            );
            productUomCacheRef.current.set(cacheKey, request);
            return request;
        },
        [deferredProductDataCacheContextKey]
    );

    React.useEffect(() => {
        if (!MsDyn365.isBrowser || typeof window === 'undefined') {
            return;
        }

        let isCurrentPrefetch = true;
        const productsRequiringAttributes = activeProductsRef.current
            .filter(product => !hasRequiredSearchAttributes(product))
            .map(product => product.RecordId)
            .filter((productId): productId is number => Boolean(productId));
        const initialQueue = productsRequiringAttributes.slice(0, INITIAL_ATTRIBUTE_PREFETCH_COUNT);
        const backgroundQueue = productsRequiringAttributes.slice(INITIAL_ATTRIBUTE_PREFETCH_COUNT);

        const runBoundedQueue = async (queue: number[]): Promise<void> => {
            let nextIndex = 0;
            const worker = async (): Promise<void> => {
                while (isCurrentPrefetch) {
                    const productId = queue[nextIndex++];
                    if (!productId) {
                        return;
                    }

                    try {
                        await loadProductAttributes(productId);
                    } catch {
                        // The card retains its existing failed-attribute fallback; continue the queue.
                    }
                }
            };

            const workerCount = Math.min(ATTRIBUTE_PREFETCH_CONCURRENCY, queue.length);
            await Promise.all(Array.from({ length: workerCount }, () => worker()));
        };

        void runBoundedQueue(initialQueue).then(() => {
            if (isCurrentPrefetch) {
                return runBoundedQueue(backgroundQueue);
            }
            return undefined;
        });

        return () => {
            isCurrentPrefetch = false;
        };
    }, [activeProductIdsKey, deferredProductDataCacheContextKey, loadProductAttributes]);

    const deferredProductDataContext: IPlpDeferredProductDataContext = {
        loadProductAttributes,
        loadProductUnitsOfMeasure
    };
    const activeProductIdsRef = React.useRef<ReadonlySet<number>>(new Set<number>());
    activeProductIdsRef.current = new Set(activeProductIds);
    const pricingUomByProductRef = React.useRef<Map<number, string | undefined>>(new Map());
    const latestPricingRequestByProductRef = React.useRef<Map<number, IProductPricingRequest>>(new Map());
    const productPriceCacheRef = React.useRef<Map<string, IProductPriceCacheEntry>>(new Map());
    const pricingRequestIdRef = React.useRef(0);
    const [pricingUomByProduct, setPricingUomByProduct] = React.useState<Map<number, string | undefined>>(new Map());
    const [pricingByProduct, setPricingByProduct] = React.useState<Map<number, IProductPricingState>>(new Map());
    const onPricingUomResolved = React.useCallback<PricingUomResolvedHandler>(
        (productId, unitOfMeasure) => {
            if (!isAuthenticated || !activeProductIdsRef.current.has(productId)) {
                return;
            }

            const resolvedUnitOfMeasure = unitOfMeasure || undefined;
            if (
                pricingUomByProductRef.current.has(productId) &&
                isSamePricingUom(pricingUomByProductRef.current.get(productId), resolvedUnitOfMeasure)
            ) {
                return;
            }

            pricingUomByProductRef.current.set(productId, resolvedUnitOfMeasure);
            latestPricingRequestByProductRef.current.delete(productId);
            setPricingUomByProduct(currentUoms => {
                const nextUoms = new Map(currentUoms);
                nextUoms.set(productId, resolvedUnitOfMeasure);
                return nextUoms;
            });
            const priceCacheKey = `${deferredProductDataCacheContextKey}|price|${productId}|${normalizePricingUom(resolvedUnitOfMeasure) ??
                ''}`;
            const cachedPrice = resolvedUnitOfMeasure ? productPriceCacheRef.current.get(priceCacheKey) : undefined;
            setPricingByProduct(currentPricing => {
                const nextPricing = new Map(currentPricing);
                nextPricing.set(productId, {
                    unitOfMeasure: resolvedUnitOfMeasure,
                    status: cachedPrice?.status ?? (resolvedUnitOfMeasure ? 'loading' : 'failed'),
                    ...(cachedPrice?.price ? { price: cachedPrice.price } : {})
                });
                return nextPricing;
            });
        },
        [deferredProductDataCacheContextKey, isAuthenticated]
    );
    const pricingPairs: IProductPricingPair[] = activeProductIds
        .map(productId => ({ productId, unitOfMeasure: pricingUomByProduct.get(productId) }))
        .filter((pair): pair is IProductPricingPair => Boolean(pair.unitOfMeasure));
    const pendingPricingPairs = pricingPairs.filter(pair => {
        const currentPricing = pricingByProduct.get(pair.productId);
        if (
            isSamePricingUom(currentPricing?.unitOfMeasure, pair.unitOfMeasure) &&
            (currentPricing?.status === 'resolved' || currentPricing?.status === 'failed')
        ) {
            return false;
        }

        const latestRequest = latestPricingRequestByProductRef.current.get(pair.productId);
        return !latestRequest || !isSamePricingUom(latestRequest.unitOfMeasure, pair.unitOfMeasure);
    });
    const pendingPricingRequestKey = JSON.stringify(pendingPricingPairs);
    const getPriceCacheKey = React.useCallback(
        (productId: number, unitOfMeasure?: string): string =>
            `${deferredProductDataCacheContextKey}|price|${productId}|${normalizePricingUom(unitOfMeasure) ?? ''}`,
        [deferredProductDataCacheContextKey]
    );
    const pricingContext: IPlpPricingContext = {
        uomByProduct: pricingUomByProduct,
        pricingByProduct,
        onPricingUomResolved
    };

    React.useEffect(() => {
        if (!MsDyn365.isBrowser || typeof window === 'undefined') {
            return;
        }

        const updateSelectedStore = (): void => {
            setSelectedStoreId(getPreferredStoreId());
        };
        const handleStorage = (event: StorageEvent): void => {
            if (event.key === '_msdyn365__preferredStore_') {
                updateSelectedStore();
            }
        };

        updateSelectedStore();
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    React.useEffect(() => {
        let isCurrentRequest = true;
        const requestKey = selectedStoreAvailabilityRequestKey;

        setSelectedStoreAvailabilityState({
            requestKey,
            availabilityByProduct: new Map<number, ProductWarehouseInventoryAvailability>()
        });

        if (!props.context.app.config.enableStockCheck || !selectedStoreId || activeProductIds.length === 0) {
            return () => {
                isCurrentRequest = false;
            };
        }

        const searchArea: SearchArea = {
            Latitude: 0,
            Longitude: 0,
            Radius: 0,
            DistanceUnitValue: 0
        };
        const searchCriteria = createInventoryAvailabilitySearchCriteria(
            props.context.actionContext,
            activeProductIds,
            false,
            true,
            searchArea,
            2
        );

        void getEstimatedAvailabilityAsync({ callerContext: props.context.actionContext }, searchCriteria)
            .then(response => {
                if (!isCurrentRequest) {
                    return;
                }

                const availabilityByProduct = new Map<number, ProductWarehouseInventoryAvailability>();
                response?.ProductWarehouseInventoryAvailabilities?.forEach(availability => {
                    const productId = availability.ProductId;
                    if (
                        productId !== undefined &&
                        availability.InventLocationId === selectedStoreId &&
                        !availabilityByProduct.has(productId)
                    ) {
                        availabilityByProduct.set(productId, availability);
                    }
                });
                setSelectedStoreAvailabilityState({ requestKey, availabilityByProduct });
            })
            .catch((error: Error) => {
                if (!isCurrentRequest) {
                    return;
                }

                console.error(error.message);
                setSelectedStoreAvailabilityState({
                    requestKey,
                    availabilityByProduct: new Map<number, ProductWarehouseInventoryAvailability>()
                });
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [activeProductIdsKey, props.context.actionContext, props.context.app.config.enableStockCheck, selectedStoreId]);

    React.useEffect(() => {
        const activeProductIdSet = new Set(activeProductIds);
        pricingUomByProductRef.current.forEach((_unitOfMeasure, productId) => {
            if (!activeProductIdSet.has(productId)) {
                pricingUomByProductRef.current.delete(productId);
                latestPricingRequestByProductRef.current.delete(productId);
            }
        });
        setPricingUomByProduct(currentUoms => {
            if (currentUoms.size === 0 || Array.from(currentUoms.keys()).every(productId => activeProductIdSet.has(productId))) {
                return currentUoms;
            }

            const activeUoms = new Map<number, string | undefined>();
            currentUoms.forEach((unitOfMeasure, productId) => {
                if (activeProductIdSet.has(productId)) {
                    activeUoms.set(productId, unitOfMeasure);
                }
            });
            return activeUoms;
        });
        setPricingByProduct(currentPricing => {
            if (currentPricing.size === 0 || Array.from(currentPricing.keys()).every(productId => activeProductIdSet.has(productId))) {
                return currentPricing;
            }

            const activePricing = new Map<number, IProductPricingState>();
            currentPricing.forEach((pricing, productId) => {
                if (activeProductIdSet.has(productId)) {
                    activePricing.set(productId, pricing);
                }
            });
            return activePricing;
        });
    }, [activeProductIdsKey]);

    React.useEffect(() => {
        if (!isAuthenticated || pendingPricingPairs.length === 0) {
            return;
        }

        const requestId = ++pricingRequestIdRef.current;
        const requestedPricingPairs = pendingPricingPairs;
        requestedPricingPairs.forEach(pair => {
            latestPricingRequestByProductRef.current.set(pair.productId, {
                requestId,
                unitOfMeasure: pair.unitOfMeasure
            });
        });

        const projectDomain: ProjectionDomain = {
            ChannelId: props.context.request.apiSettings.channelId,
            CatalogId: props.context.request.apiSettings.catalogId
        };
        const priceLookupContext: PriceLookupContext = {
            HeaderContext: undefined,
            LineContexts: requestedPricingPairs.map(pair => ({
                ProductRecordId: pair.productId,
                UnitOfMeasureSymbol: pair.unitOfMeasure
            }))
        };
        const getActivePricesInput = new GetActivePricesInput(
            projectDomain,
            // pricingPairs.map(pair => pair.productId),
            undefined,
            undefined,
            undefined,
            undefined,
            true,
            false,
            false,
            priceLookupContext
        );

        void getActivePricesAction(getActivePricesInput, props.context.actionContext)
            .then(result => {
                if (!result || result.length === 0) {
                    console.warn('No active prices returned from API');
                    setPricingByProduct(currentPricing => {
                        const nextPricing = new Map(currentPricing);
                        let hasPricingUpdates = false;
                        requestedPricingPairs.forEach(pair => {
                            const latestRequest = latestPricingRequestByProductRef.current.get(pair.productId);
                            const currentPricingForProduct = currentPricing.get(pair.productId);
                            if (
                                latestRequest?.requestId === requestId &&
                                isSamePricingUom(latestRequest.unitOfMeasure, pair.unitOfMeasure) &&
                                isSamePricingUom(currentPricingForProduct?.unitOfMeasure, pair.unitOfMeasure) &&
                                activeProductIdsRef.current.has(pair.productId)
                            ) {
                                productPriceCacheRef.current.set(getPriceCacheKey(pair.productId, pair.unitOfMeasure), {
                                    status: 'failed'
                                });
                                nextPricing.set(pair.productId, {
                                    unitOfMeasure: pair.unitOfMeasure,
                                    status: 'failed'
                                });
                                hasPricingUpdates = true;
                            }
                        });
                        return hasPricingUpdates ? nextPricing : currentPricing;
                    });
                    return;
                }

                const pricesByProduct = new Map<number, ProductPrice[]>();
                result.forEach(price => {
                    if (price.ProductId === undefined) {
                        return;
                    }

                    const productPrices = pricesByProduct.get(price.ProductId) ?? [];
                    productPrices.push(price);
                    pricesByProduct.set(price.ProductId, productPrices);
                });

                setPricingByProduct(currentPricing => {
                    const nextPricing = new Map(currentPricing);
                    let hasPricingUpdates = false;
                    requestedPricingPairs.forEach(pair => {
                        const productPrices = pricesByProduct.get(pair.productId) ?? [];
                        // if (pair.productId === 5637154422) {
                        //     console.log('CW ACTIVE PRICE DEBUG', {
                        //         requestedUom: pair.unitOfMeasure,
                        //         productPrices: productPrices.map(price => ({
                        //             ProductId: price.ProductId,
                        //             UnitOfMeasure: price.UnitOfMeasure,
                        //             CustomerContextualPrice: price.CustomerContextualPrice,
                        //             BasePrice: price.BasePrice,
                        //             AdjustedPrice: price.AdjustedPrice
                        //         }))
                        //     });
                        // }
                        const exactUomPrice = productPrices.find(price => isSamePricingUom(price.UnitOfMeasure, pair.unitOfMeasure));
                        const soleProductPrice = productPrices.length === 1 ? productPrices[0] : undefined;
                        // const productPrice =
                        //     exactUomPrice ?? (soleProductPrice && !soleProductPrice.UnitOfMeasure ? soleProductPrice : undefined);
                        // productPriceCacheRef.current.set(getPriceCacheKey(pair.productId, pair.unitOfMeasure), {
                        //     status: productPrice ? 'resolved' : 'failed',
                        //     ...(productPrice ? { price: productPrice } : {})
                        // });
                        const productPrice =
                            exactUomPrice ??
                            (soleProductPrice &&
                            (!soleProductPrice.UnitOfMeasure || normalizePricingUom(soleProductPrice.UnitOfMeasure) === 'LB')
                                ? soleProductPrice
                                : undefined);
                        const latestRequest = latestPricingRequestByProductRef.current.get(pair.productId);
                        const currentPricingForProduct = currentPricing.get(pair.productId);
                        if (
                            latestRequest?.requestId !== requestId ||
                            !isSamePricingUom(latestRequest.unitOfMeasure, pair.unitOfMeasure) ||
                            !isSamePricingUom(currentPricingForProduct?.unitOfMeasure, pair.unitOfMeasure) ||
                            !activeProductIdsRef.current.has(pair.productId)
                        ) {
                            return;
                        }

                        nextPricing.set(
                            pair.productId,
                            productPrice
                                ? {
                                      unitOfMeasure: pair.unitOfMeasure,
                                      status: 'resolved',
                                      price: productPrice
                                  }
                                : {
                                      unitOfMeasure: pair.unitOfMeasure,
                                      status: 'failed'
                                  }
                        );
                        hasPricingUpdates = true;
                    });
                    return hasPricingUpdates ? nextPricing : currentPricing;
                });
            })
            .catch((error: Error) => {
                console.error('Error fetching active prices:', error);
                setPricingByProduct(currentPricing => {
                    const nextPricing = new Map(currentPricing);
                    let hasPricingUpdates = false;
                    requestedPricingPairs.forEach(pair => {
                        const latestRequest = latestPricingRequestByProductRef.current.get(pair.productId);
                        const currentPricingForProduct = currentPricing.get(pair.productId);
                        if (
                            latestRequest?.requestId === requestId &&
                            isSamePricingUom(latestRequest.unitOfMeasure, pair.unitOfMeasure) &&
                            isSamePricingUom(currentPricingForProduct?.unitOfMeasure, pair.unitOfMeasure) &&
                            activeProductIdsRef.current.has(pair.productId)
                        ) {
                            productPriceCacheRef.current.set(getPriceCacheKey(pair.productId, pair.unitOfMeasure), { status: 'failed' });
                            nextPricing.set(pair.productId, {
                                unitOfMeasure: pair.unitOfMeasure,
                                status: 'failed'
                            });
                            hasPricingUpdates = true;
                        }
                    });
                    return hasPricingUpdates ? nextPricing : currentPricing;
                });
            });
    }, [
        isAuthenticated,
        pendingPricingRequestKey,
        props.context.actionContext,
        props.context.request.apiSettings.catalogId,
        props.context.request.apiSettings.channelId,
        getPriceCacheKey
    ]);

    // @ts-ignore -- useStickyFilter is supplied by the custom module configuration extension.
    const useStickyFilter = Boolean(props.config.useStickyFilter);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
    const isStickyHeaderEnabled = Boolean(props.context.app.config.enableStickyHeader);
    useEffect(() => {
        if (!useStickyFilter || !MsDyn365.isBrowser) {
            return;
        }

        return stickyFilterSetup(props.isMobile, isStickyHeaderEnabled);
    }, [isStickyHeaderEnabled, props.isMobile, useStickyFilter]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const enqueueAddToCart = React.useCallback<AddToCartQueueHandler>(
        cartLines => {
            return new Promise<void>((resolve, reject) => {
                const execute = async (): Promise<void> => {
                    const cartState = await getCartState(props.context.actionContext);
                    const newCart = await addCartLinesAsync(
                        { callerContext: props.context.actionContext },
                        cartState.cart.Id,
                        cartLines,
                        cartState.cart.Version
                    );
                    const updateResult = await cartState.updateCart({ newCartObject: newCart });
                    if (updateResult.status !== 'SUCCESS') {
                        throw new Error(updateResult.errorDetails?.LocalizedMessage || 'Unable to update the cart.');
                    }
                };

                addToCartQueue.push({ execute, resolve, reject });
                void processAddToCartQueue();
            });
        },
        [props.context.actionContext]
    );

    const handlePaginationClick = React.useCallback<React.MouseEventHandler<HTMLElement>>(event => {
        if (!isProcessingAddToCartQueue && addToCartQueue.length === 0) {
            return;
        }

        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const paginationLink = target.closest<HTMLAnchorElement>('a[href]');
        if (!paginationLink) {
            return;
        }

        event.preventDefault();
        const destinationUrl = paginationLink.href;

        void waitForAddToCartQueueToDrain().then(() => {
            window.location.assign(destinationUrl);
        });
    }, []);

    const toggle = React.useCallback(() => {
        setIsExpanded(currentValue => !currentValue);
    }, []);

    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, toggle);
    const query = props.context.actionContext.requestContext.query;
    const isRecoSearchPage = query?.recommendation;
    // const hasSearchKeyword = Boolean(query?.q?.trim());
    // @ts-ignore -- enableProdutDescription is a custom module configuration.
    // const shouldUseCustomProductRenderer = hasSearchKeyword || props.config.enableProdutDescription;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {renderTitleCount(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {/* {shouldUseCustomProductRenderer ? renderProducts(props, enqueueAddToCart) : renderProductCards(props)} */}
                {renderProducts(
                    props,
                    enqueueAddToCart,
                    selectedStoreId,
                    selectedStoreAvailabilityByProduct,
                    pricingContext,
                    deferredProductDataContext
                )}
                {renderQueueAwarePagination(pagination, handlePaginationClick)}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && renderTitleCount(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                    <Node className='ms-refiner-heading_title'>
                        <Button
                            className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                            aria-label='Filter'
                            onClick={toggle}
                            aria-expanded={isExpanded}
                            tabIndex={0}
                        >
                            {/* @ts-ignore */}
                            {resources.filterText}
                        </Button>
                        <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                        </div>
                    </Node>
                    {refineMenu && isExpanded && renderRefiner(refineMenu)}
                    <Node className='ms-refine__footer'>
                        {refineMenu && isExpanded && renderRefinerFooter(refineMenu, toggle, resources)}
                    </Node>
                </Node>

                <Node {...ProductSectionContainer}>
                    {errorMessage}
                    {/* {shouldUseCustomProductRenderer ? renderProducts(props, enqueueAddToCart) : renderProductCards(props)} */}
                    {renderProducts(
                        props,
                        enqueueAddToCart,
                        selectedStoreId,
                        selectedStoreAvailabilityByProduct,
                        pricingContext,
                        deferredProductDataContext
                    )}
                    {renderQueueAwarePagination(pagination, handlePaginationClick)}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Refiner Footer.
 * @param props - The Refine Menu view props.
 * @param handler - The Refine menu toggle function.
 * @param resources - Resources to get the done text and aria label.
 * @returns The JSX Element.
 */
const renderRefinerFooter = (
    props: IRefineMenuViewProps,
    handler: MouseEventHandler,
    resources: ISearchResultContainerResources
): JSX.Element | null => {
    const { refiners } = props;
    if (refiners) {
        return (
            <Node className='ms-refine__footer__done'>
                {/* @ts-ignore */}
                <Node tag='button' className='ms-refine-filter-done' aria-label={resources.doneAriaLabel} tabIndex={0} onClick={handler}>
                    {/* @ts-ignore */}
                    {resources.doneText}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h1' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
